import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { catchError, finalize, Subscription, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { UserProfile } from '../../core/models';
import { OrderService, SeoService, ToastService, ValidationService, VoucherService } from '../../core/services';
import { voucherImage } from '../../core/helpers';

@Component({
  selector: 'gs-gift-card',
  templateUrl: './gift-card.component.html',
  standalone: false
})

export class GiftCardComponent implements OnInit, OnDestroy {

  userId: number;
  voucherSlug: string;
  // amount: any = 0;
  voucher: any;

  form: FormGroup = this.formBuilder.group({
    amount: [null, [Validators.required, Validators.min(1)]],
    quantity: [1, [Validators.required, Validators.min(1)]]
  });

  private subscriptions: Subscription[] = [];

  get controls() {
    return this.form.controls;
  }

  get rangeGiftValues(): number[] {
    return this.voucher &&
      this.voucher.fixedRange &&
      this.voucher.fixedRange
        .split('|')
        .sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  get voucherImageUrl(): string {
    return voucherImage(this.voucher?.image);
  }

  get charityCategoryId(): number {
    return environment.charityCategoryId;
  }

  get giftCardUrl(): string {
    return this.document.location.href;
  }

  get voucherDescription(): string {
    return this.voucher?.description.replaceAll('<img', '<img class="rounded-5"');
  }

  constructor(
    private voucherService: VoucherService,
    private orderService: OrderService,
    private toastService: ToastService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private formBuilder: FormBuilder,
    public vs: ValidationService,
    private seoService: SeoService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.userId = user?.id;
        })
    );

    this.voucherSlug = this.route.snapshot.paramMap.get('slug');

    this.getVoucher();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private getVoucher() {
    this.subscriptions.push(
      this.voucherService.getVoucherBySlug(this.voucherSlug)
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((response: any) => {
          this.voucher = response;

          // Custom SEO tags
          if (this.voucher.partnerCategories.some(z => z.id == environment.charityCategoryId)) {
            this.seoService.setTitle(`Sustine ${this.voucher.partnerName} prin GiftShare – Doneaza Online`);
            this.seoService.setDescription(`Contribuie la misiunea ${this.voucher.partnerName} cu o donatie rapida prin GiftShare. Implica-te online si sprijina direct cauze sociale si umanitare.`);
          } else {
            this.seoService.setTitle(`Cumpara un ${this.voucher.name} Online`);
            this.seoService.setDescription(`Ofera un ${this.voucher.name} prin GiftShare. Trimite un voucher cadou online, simplu si rapid, perfect pentru orice ocazie.`);
          }
          this.seoService.setImage(this.voucherImageUrl);

          if (response.rangeMinValue && response.rangeMaxValue) {
            this.controls.amount.setValidators([Validators.required, Validators.min(response.rangeMinValue), Validators.max(response.rangeMaxValue)]);
            this.controls.amount.updateValueAndValidity();
          }

          this.voucher.optionTypes.forEach(optionType => {
            this.form.addControl(optionType.name, this.formBuilder.control(null, Validators.required))
          });
        })
    );
  }

  changeQuantityClick(value) {
    if (value < 1) {
      return;
    }

    this.form.patchValue({ quantity: value });
  }

  onSubmit() {
    if (!this.form.valid) {
      this.vs.triggerValidation(this.form);
      return;
    }

    const model = {
      voucherId: this.voucher.id,
      quantity: this.form.value.quantity,
      amount: this.form.value.amount,
      optionIds: this.voucher.optionTypes.map(x => this.form.value[x.name])
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.orderService.createCartItem(model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.router.navigate(['/user/cos']);
        })
    );

  }

}
